import React, { useState, useRef } from 'react';
import { Upload, Download, Users, AlertCircle, CheckCircle, X, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import alunosService from '../services/alunosService';

const ImportarAlunos = ({ onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef();

  // Mapeamento das colunas da planilha para campos do banco
  const columnMapping = {
    // Variações possíveis de CPF
    'CPF': 'cpf',
    'Cpf': 'cpf',
    'cpf': 'cpf',
    
    // Variações de RA
    'RA': 'ra',
    'Ra': 'ra', 
    'ra': 'ra',
    
    // Variações de Nome
    'NOME': 'nome',
    'Nome': 'nome',
    'nome': 'nome',
    'NOME COMPLETO': 'nome',
    'Nome Completo': 'nome',
    
    // Variações de Email
    'E-MAIL': 'email',
    'EMAIL': 'email',
    'Email': 'email',
    'email': 'email',
    'E-mail': 'email',
    'e-mail': 'email',
    
    // Endereço
    'ENDEREÇO': 'logradouro',
    'ENDERECO': 'logradouro',
    'Endereço': 'logradouro',
    'Endereco': 'logradouro',
    'endereço': 'logradouro',
    'endereco': 'logradouro',
    'LOGRADOURO': 'logradouro',
    'Logradouro': 'logradouro',
    
    // Número
    'NUMERO': 'numero',
    'NÚMERO': 'numero',
    'Numero': 'numero',
    'Número': 'numero',
    'numero': 'numero',
    'número': 'numero',
    'N°': 'numero',
    'Nº': 'numero',
    
    // Bairro
    'BAIRRO': 'bairro',
    'Bairro': 'bairro',
    'bairro': 'bairro',
    
    // Cidade
    'CIDADE': 'cidade',
    'Cidade': 'cidade',
    'cidade': 'cidade',
    
    // Estado
    'ESTADO': 'estado',
    'Estado': 'estado',
    'estado': 'estado',
    'UF': 'estado',
    'uf': 'estado',
    
    // Telefone/Celular
    'CELULAR': 'telefone',
    'Celular': 'telefone',
    'celular': 'telefone',
    'TELEFONE': 'telefone',
    'Telefone': 'telefone',
    'telefone': 'telefone',
    'FONE': 'telefone',
    'Fone': 'telefone',
    
    // Data de Nascimento
    'DATA NASC': 'data_nascimento',
    'DATA NASCIMENTO': 'data_nascimento',
    'Data Nasc': 'data_nascimento',
    'Data Nascimento': 'data_nascimento',
    'data nasc': 'data_nascimento',
    'data nascimento': 'data_nascimento',
    'NASCIMENTO': 'data_nascimento',
    'Nascimento': 'data_nascimento',
    
    // Escola
    'ESCOLA': 'escola_nome',
    'Escola': 'escola_nome',
    'escola': 'escola_nome',
    'INSTITUIÇÃO': 'escola_nome',
    'Instituição': 'escola_nome',
    'INSTITUICAO': 'escola_nome',
    'Instituicao': 'escola_nome',
    
    // Ano/Série
    'ANO': 'ano_nome',
    'Ano': 'ano_nome',
    'ano': 'ano_nome',
    'SÉRIE': 'ano_nome',
    'SERIE': 'ano_nome',
    'Série': 'ano_nome',
    'Serie': 'ano_nome',
    'série': 'ano_nome',
    'serie': 'ano_nome',
    'TURMA': 'ano_nome',
    'Turma': 'ano_nome',
    'turma': 'ano_nome',
    
    // Data de cadastro
    'DATA': 'data_cadastro',
    'Data': 'data_cadastro',
    'data': 'data_cadastro'
  };

  // Processar arquivo Excel/CSV
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setErrors([]);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Debug: mostrar colunas encontradas
        if (jsonData.length > 0) {
          const columns = Object.keys(jsonData[0]);
          const cleanColumns = columns.map(col => col.trim());
          
          console.log('Colunas originais na planilha:', columns);
          console.log('Colunas após limpeza:', cleanColumns);
          
          // Verificar quais colunas são reconhecidas após limpeza
          const recognizedColumns = cleanColumns.filter(col => columnMapping[col]);
          const unrecognizedColumns = cleanColumns.filter(col => !columnMapping[col]);
          
          console.log('Colunas reconhecidas:', recognizedColumns);
          console.log('Colunas não reconhecidas:', unrecognizedColumns);
          
          if (unrecognizedColumns.length > 0) {
            setErrors([
              `Colunas não reconhecidas: ${unrecognizedColumns.join(', ')}`,
              `Verifique se os nomes das colunas estão corretos na planilha`
            ]);
          }
        }
        
        setData(jsonData);
      } catch (error) {
        setErrors([`Erro ao ler arquivo: ${error.message}`]);
      }
    };
    reader.readAsBinaryString(uploadedFile);
  };

  // Gerar senha a partir do CPF (apenas números)
  const generatePassword = (cpf) => {
    return cpf.replace(/\D/g, ''); // Remove tudo que não é número
  };

  // Validar e mapear dados da planilha
  const validateAndMapData = async () => {
    const mappedData = [];
    const validationErrors = [];
    
    // Buscar escolas e anos escolares para mapeamento
    const [escolas, anosEscolares] = await Promise.all([
      alunosService.getEscolas(),
      alunosService.getAnosEscolares()
    ]);

    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 porque Excel começa em 1 e tem header
      const mappedRow = {};
      
      // Mapear colunas (removendo espaços dos nomes das colunas)
      Object.keys(row).forEach(originalCol => {
        const cleanCol = originalCol.trim(); // Remove espaços do nome da coluna
        const dbField = columnMapping[cleanCol];
        if (dbField) {
          mappedRow[dbField] = String(row[originalCol] || '').trim();
        }
      });

      // Validações obrigatórias
      if (!mappedRow.cpf) {
        validationErrors.push(`Linha ${rowNumber}: CPF é obrigatório`);
      }
      if (!mappedRow.nome) {
        validationErrors.push(`Linha ${rowNumber}: Nome é obrigatório`);
      }
      if (!mappedRow.email) {
        validationErrors.push(`Linha ${rowNumber}: Email é obrigatório`);
      }

      // Validar formato CPF
      const cpfNumbers = mappedRow.cpf ? mappedRow.cpf.replace(/\D/g, '') : '';
      if (cpfNumbers.length !== 11) {
        validationErrors.push(`Linha ${rowNumber}: CPF deve ter 11 dígitos`);
      }

      // Validar email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (mappedRow.email && !emailRegex.test(mappedRow.email)) {
        validationErrors.push(`Linha ${rowNumber}: Email inválido`);
      }

      // Buscar escola por nome
      const escolaNome = String(mappedRow.escola_nome || '').trim();
      if (!escolaNome) {
        validationErrors.push(`Linha ${rowNumber}: Nome da escola é obrigatório`);
      } else {
        const escola = escolas.find(e => 
          e.nome.toLowerCase().includes(escolaNome.toLowerCase())
        );
        if (!escola) {
          validationErrors.push(`Linha ${rowNumber}: Escola "${escolaNome}" não encontrada`);
        } else {
          mappedRow.escola_id = escola.id;
        }
      }

      // Buscar ano escolar por nome
      const anoNome = String(mappedRow.ano_nome || '').trim();
      if (!anoNome) {
        validationErrors.push(`Linha ${rowNumber}: Nome do ano escolar é obrigatório`);
      } else {
        const anoEscolar = anosEscolares.find(a => 
          a.nome.toLowerCase().includes(anoNome.toLowerCase())
        );
        if (!anoEscolar) {
          validationErrors.push(`Linha ${rowNumber}: Ano "${anoNome}" não encontrado`);
        } else {
          mappedRow.ano_escolar_id = anoEscolar.id;
        }
      }

      // Gerar senha do CPF
      mappedRow.senha = generatePassword(mappedRow.cpf || '');

      // Formatações
      if (cpfNumbers.length === 11) {
        mappedRow.cpf = cpfNumbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
      
      // Telefone (priorizar celular)
      if (row['CELULAR']) {
        mappedRow.telefone = row['CELULAR'];
      } else if (row['TELEFONE']) {
        mappedRow.telefone = row['TELEFONE'];
      }

      // Formatar data de nascimento
      if (mappedRow.data_nascimento) {
        try {
          const date = new Date(mappedRow.data_nascimento);
          mappedRow.data_nascimento = date.toISOString().split('T')[0];
        } catch (error) {
          validationErrors.push(`Linha ${rowNumber}: Data de nascimento inválida`);
        }
      }

      // Definir período padrão (pode ser ajustado)
      mappedRow.periodo = 'manhã';
      mappedRow.ativo = true;

      // Remover campos temporários
      delete mappedRow.escola_nome;
      delete mappedRow.ano_nome;
      delete mappedRow.data_cadastro;

      mappedData.push(mappedRow);
    });

    return { mappedData, validationErrors };
  };

  // Processar importação
  const handleImport = async () => {
    if (!data.length) {
      setErrors(['Nenhum dado para importar']);
      return;
    }

    setProcessing(true);
    setProgress(0);
    setErrors([]);

    try {
      // Validar dados
      const { mappedData, validationErrors } = await validateAndMapData();
      
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setProcessing(false);
        return;
      }

      // Processar em lotes
      const batchSize = 10;
      const batches = [];
      for (let i = 0; i < mappedData.length; i += batchSize) {
        batches.push(mappedData.slice(i, i + batchSize));
      }

      let successCount = 0;
      let errorCount = 0;
      const importErrors = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        for (let studentIndex = 0; studentIndex < batch.length; studentIndex++) {
          const studentData = batch[studentIndex];
          
          try {
            // Criar aluno
            const savedAluno = await alunosService.create(studentData);
            
            // Criar usuário de autenticação
            try {
              await alunosService.createAuthUser(
                studentData.email, 
                studentData.senha, 
                savedAluno
              );
            } catch (authError) {
              console.warn(`Erro ao criar auth para ${studentData.email}:`, authError);
              // Continua mesmo se falhar o auth
            }
            
            successCount++;
          } catch (error) {
            errorCount++;
            importErrors.push(`${studentData.nome} (${studentData.email}): ${error.message}`);
          }
          
          // Atualizar progresso
          const totalProcessed = (batchIndex * batchSize) + studentIndex + 1;
          setProgress(Math.round((totalProcessed / mappedData.length) * 100));
        }
      }

      // Resultados finais
      setResults({
        total: mappedData.length,
        success: successCount,
        errors: errorCount,
        errorDetails: importErrors
      });

      if (successCount > 0 && onImportComplete) {
        onImportComplete();
      }

    } catch (error) {
      setErrors([`Erro durante importação: ${error.message}`]);
    } finally {
      setProcessing(false);
    }
  };

  // Baixar template
  const downloadTemplate = () => {
    const template = [{
      'CPF': '12345678901',
      'RA': 'RA001',
      'NOME': 'João Silva',
      'E-MAIL': 'joao@email.com',
      'ENDEREÇO': 'Rua das Flores, 123',
      'NUMERO': '123',
      'BAIRRO': 'Centro',
      'CIDADE': 'São Paulo',
      'ESTADO': 'SP',
      'CELULAR': '(11) 99999-9999',
      'TELEFONE': '(11) 3333-3333',
      'DATA NASC': '2000-01-15',
      'ESCOLA': 'Escola Municipal',
      'ANO': '1º Ano',
      'DATA': '2024-01-01'
    }];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'template_importacao_alunos.xlsx');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Importar Alunos em Massa</h2>
          <button className="btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Template Download */}
          <div className="import-section">
            <h3>1. Baixar Template</h3>
            <p>Baixe o template Excel com as colunas corretas:</p>
            <button className="btn-secondary" onClick={downloadTemplate}>
              <Download size={16} />
              Baixar Template Excel
            </button>
          </div>

          {/* File Upload */}
          <div className="import-section">
            <h3>2. Selecionar Arquivo</h3>
            <div className="file-upload-area" onClick={() => fileInputRef.current?.click()}>
              <FileSpreadsheet size={48} />
              <p>{file ? file.name : 'Clique para selecionar arquivo Excel (.xlsx)'}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Preview */}
          {data.length > 0 && (
            <div className="import-section">
              <h3>3. Prévia dos Dados</h3>
              <div className="data-preview">
                <p><strong>{data.length}</strong> registros encontrados</p>
                <div className="preview-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>CPF</th>
                        <th>Escola</th>
                        <th>Ano</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.slice(0, 5).map((row, index) => (
                        <tr key={index}>
                          <td>{row.NOME}</td>
                          <td>{row['E-MAIL']}</td>
                          <td>{row.CPF}</td>
                          <td>{row.ESCOLA}</td>
                          <td>{row.ANO}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.length > 5 && (
                    <p className="preview-more">... e mais {data.length - 5} registros</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Progress */}
          {processing && (
            <div className="import-section">
              <h3>Processando...</h3>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <p>{progress}% concluído</p>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="import-section">
              <h3>Resultados da Importação</h3>
              <div className="results-summary">
                <div className="result-item success">
                  <CheckCircle size={20} />
                  <span>{results.success} alunos criados com sucesso</span>
                </div>
                {results.errors > 0 && (
                  <div className="result-item error">
                    <AlertCircle size={20} />
                    <span>{results.errors} erros encontrados</span>
                  </div>
                )}
              </div>
              
              {results.errorDetails.length > 0 && (
                <div className="error-details">
                  <h4>Detalhes dos Erros:</h4>
                  <ul>
                    {results.errorDetails.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="import-section">
              <h3>Erros de Validação</h3>
              <div className="error-list">
                {errors.map((error, index) => (
                  <div key={index} className="error-item">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-primary" 
            onClick={handleImport}
            disabled={!data.length || processing}
          >
            <Upload size={16} />
            {processing ? 'Importando...' : 'Importar Alunos'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-content.large {
          max-width: 900px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .import-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .import-section:last-child {
          border-bottom: none;
        }

        .import-section h3 {
          margin: 0 0 12px 0;
          color: #1e293b;
          font-size: 16px;
          font-weight: 600;
        }

        .file-upload-area {
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          padding: 32px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s ease;
        }

        .file-upload-area:hover {
          border-color: #3b82f6;
          background: #f0f9ff;
        }

        .data-preview {
          background: #f8fafc;
          border-radius: 8px;
          padding: 16px;
        }

        .preview-table {
          overflow-x: auto;
          margin-top: 12px;
        }

        .preview-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 6px;
          overflow: hidden;
        }

        .preview-table th,
        .preview-table td {
          padding: 8px 12px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }

        .preview-table th {
          background: #f1f5f9;
          font-weight: 600;
          color: #475569;
        }

        .preview-more {
          margin-top: 8px;
          color: #64748b;
          font-style: italic;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
          margin: 12px 0;
        }

        .progress-fill {
          height: 100%;
          background: #3b82f6;
          transition: width 0.3s ease;
        }

        .results-summary {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .result-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 6px;
        }

        .result-item.success {
          background: #f0fdf4;
          color: #166534;
        }

        .result-item.error {
          background: #fef2f2;
          color: #dc2626;
        }

        .error-details {
          margin-top: 16px;
          padding: 16px;
          background: #fef2f2;
          border-radius: 6px;
        }

        .error-details h4 {
          margin: 0 0 12px 0;
          color: #dc2626;
        }

        .error-details ul {
          margin: 0;
          padding-left: 20px;
        }

        .error-details li {
          color: #dc2626;
          margin-bottom: 4px;
        }

        .error-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .error-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #fef2f2;
          border-radius: 6px;
          color: #dc2626;
        }
      `}</style>
    </div>
  );
};

export default ImportarAlunos;
